import * as React from 'react';
import { ArrowDown, ArrowUp, ChevronDown, Plus, Trash2 } from 'lucide-react';
import type {
  FormQuestion,
  FormQuestionOption,
  ProptreeEnumValue,
} from '@proptr/shared';
import { cn } from '../lib/utils.js';
import { Button } from './ui/button.js';
import { Card } from './ui/card.js';
import { Input } from './ui/input.js';
import { Label } from './ui/label.js';
import { Select, type SelectOption } from './ui/select.js';
import { Textarea } from './ui/textarea.js';

// Question list editor for a partner's form. Same question/option shape as
// the manager's form editor (apps/manager/src/pages/FormEditPage.tsx) so
// the public renderer treats partner forms exactly like brokerage forms.

export const ANSWER_TYPE_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Radio buttons', value: 'options' },
  { id: 2, name: 'Dropdown', value: 'dropdown' },
  { id: 3, name: 'Input field', value: 'input' },
  { id: 4, name: 'Text field', value: 'text' },
  { id: 5, name: 'Multi select', value: 'multi' },
];
const OPTION_BASED = new Set(['options', 'dropdown', 'multi']);

// Numeric ids, like the legacy editor — lead answers key on them.
export function newRandomId(): number {
  return Math.floor(Math.random() * 10 ** 13);
}

export function QuestionsEditor({
  questions,
  onChange,
}: {
  questions: FormQuestion[];
  onChange: (next: FormQuestion[]) => void;
}) {
  const [openIds, setOpenIds] = React.useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setOpenIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const add = () => {
    const id = newRandomId();
    onChange([...questions, { id, question: '', options: [] }]);
    setOpenIds((s) => new Set(s).add(String(id)));
  };
  const update = (i: number, patch: Partial<FormQuestion>) =>
    onChange(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const remove = (i: number) => onChange(questions.filter((_, j) => j !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= questions.length) return;
    const next = [...questions];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {questions.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-6 text-center font-mono text-[11px] text-muted-foreground">
          No questions yet — the form will only ask for the visitor’s contact.
        </div>
      )}
      {questions.map((q, i) => {
        const id = String(q.id ?? i);
        return (
          <QuestionRow
            key={id}
            index={i}
            total={questions.length}
            question={q}
            open={openIds.has(id)}
            onToggle={() => toggle(id)}
            onChange={(patch) => update(i, patch)}
            onRemove={() => remove(i)}
            onMove={(dir) => move(i, dir)}
          />
        );
      })}
      <Button type="button" variant="outline" size="sm" onClick={add}>
        <Plus />
        Add question
      </Button>
    </div>
  );
}

function QuestionRow({
  index,
  total,
  question,
  open,
  onToggle,
  onChange,
  onRemove,
  onMove,
}: {
  index: number;
  total: number;
  question: FormQuestion;
  open: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<FormQuestion>) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const options = Array.isArray(question.options) ? question.options : [];
  const answerType = String(question.answerType?.value ?? 'options');
  const updateOption = (i: number, patch: Partial<FormQuestionOption>) =>
    onChange({ options: options.map((o, j) => (j === i ? { ...o, ...patch } : o)) });

  return (
    <Card className="p-0">
      <div className="flex items-center gap-1 px-3 py-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2 py-1 text-left"
          aria-expanded={open}
        >
          <span className="shrink-0 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Q{index + 1}
          </span>
          <span className="min-w-0 flex-1 truncate text-[13px]">
            {question.question?.trim() || (
              <span className="italic text-muted-foreground/60">Untitled</span>
            )}
          </span>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
          />
        </button>
        <Button type="button" variant="ghost" size="icon" onClick={() => onMove(-1)} disabled={index === 0} aria-label="Move up">
          <ArrowUp />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={() => onMove(1)} disabled={index === total - 1} aria-label="Move down">
          <ArrowDown />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={onRemove} aria-label="Delete question">
          <Trash2 />
        </Button>
      </div>
      {open && (
        <div className="space-y-3 border-t border-[hsl(var(--hairline))] p-4">
          <Input
            value={question.question ?? ''}
            onChange={(e) => onChange({ question: e.target.value })}
            placeholder="What would you like to ask?"
          />
          <Textarea
            value={question.information ?? ''}
            onChange={(e) => onChange({ information: e.target.value })}
            placeholder="Description (optional)"
            rows={2}
            className="min-h-[2.5rem]"
          />
          <div>
            <Label>Answer type</Label>
            <Select<string>
              className="mt-1"
              options={ANSWER_TYPE_OPTIONS.map<SelectOption>((o) => ({
                value: String(o.value),
                label: o.name ?? '',
              }))}
              value={answerType}
              onChange={(v) =>
                onChange({ answerType: ANSWER_TYPE_OPTIONS.find((o) => o.value === v) })
              }
            />
          </div>
          {OPTION_BASED.has(answerType) && (
            <div className="space-y-2">
              <Label>Options</Label>
              {options.map((opt, i) => (
                <div key={String(opt.id ?? i)} className="flex items-center gap-2">
                  <Input
                    value={opt.title ?? ''}
                    onChange={(e) => updateOption(i, { title: e.target.value })}
                    placeholder="Option text"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => onChange({ options: options.filter((_, j) => j !== i) })}
                    aria-label="Delete option"
                  >
                    <Trash2 />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  onChange({ options: [...options, { id: newRandomId(), title: '' }] })
                }
              >
                <Plus />
                Add option
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
